import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
    addOrthodontistUser,
    fetchOrthodontistMembershipsByUser,
    fetchOrthodontistUsers,
    removeOrthodontistUser
} from '../services/orthodontistAccounts';
import { ORTHODONTIST_USER_MEMBERSHIPS_KEY, ORTHODONTIST_USERS_KEY } from './queryKeys';

// No pagination/search here, unlike usePracticeUsers - the backend returns
// every member of the org in one array (see orthodontist-users.service.ts).
export function useOrthodontistUsers(orthodontistId: string) {
    const query = useQuery({
        queryKey: ORTHODONTIST_USERS_KEY(orthodontistId),
        queryFn: () => fetchOrthodontistUsers(orthodontistId),
        enabled: Boolean(orthodontistId),
        staleTime: 60_000
    });

    return {
        users: query.data ?? [],
        isLoading: query.isLoading,
        isError: query.isError,
        error: query.error,
        refetch: query.refetch
    };
}

interface OrthodontistMembershipMutationInput {
    orthodontistId: string;
    userId: string;
}

// One user's org memberships, fetched by OrthodontistMultiSelect to seed its
// selected list - mirrors useOrthodontistUsers' simple GET-and-cache shape.
export function useOrthodontistMembershipsByUser(userId: string) {
    const query = useQuery({
        queryKey: ORTHODONTIST_USER_MEMBERSHIPS_KEY(userId),
        queryFn: () => fetchOrthodontistMembershipsByUser(userId),
        enabled: Boolean(userId),
        staleTime: 60_000
    });

    return {
        memberships: query.data ?? [],
        isLoading: query.isLoading
    };
}

// orthodontistId travels with the mutation so one hook serves both a single
// org's Users tab and the drawer's multi-org field (one userId, many orgs).
export function useAddOrthodontistUser() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ orthodontistId, userId }: OrthodontistMembershipMutationInput) =>
            addOrthodontistUser(orthodontistId, userId),
        onSuccess: (_data, { orthodontistId, userId }) => {
            queryClient.invalidateQueries({ queryKey: ORTHODONTIST_USERS_KEY(orthodontistId) });
            queryClient.invalidateQueries({ queryKey: ORTHODONTIST_USER_MEMBERSHIPS_KEY(userId) });
        }
    });
}

export function useRemoveOrthodontistUser() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ orthodontistId, userId }: OrthodontistMembershipMutationInput) =>
            removeOrthodontistUser(orthodontistId, userId),
        onSuccess: (_data, { orthodontistId, userId }) => {
            queryClient.invalidateQueries({ queryKey: ORTHODONTIST_USERS_KEY(orthodontistId) });
            queryClient.invalidateQueries({ queryKey: ORTHODONTIST_USER_MEMBERSHIPS_KEY(userId) });
        }
    });
}
